import React from "react";
import { Breadcrumb } from "../../components/ui";
import { useParams, useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Panel } from "primereact/panel";
import { Divider } from "primereact/divider";

import { useGetVenueByIdQuery } from "../../store/services/venues";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useTenantScope } from "../../hooks/useTenantScope";
import { BenefitsMetricsTenantScopeHeader } from "../Reporting/components/BenefitsMetricsWidgets";
import {
  getVenueStatusLabel,
  getVenueStatusSeverity,
  getAmenityLabel,
  getEventCategoryLabel,
} from "../../utils/constants/venues-constants";

export const VenueDetail = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const { data: venue, isLoading } = useGetVenueByIdQuery(venueId || "");
  const { data: tenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();

  const tenantMap = React.useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(tenants)) {
      tenants.forEach((t: any) => map.set(String(t.id), t.provider));
    }
    return map;
  }, [tenants]);

  if (isLoading) {
    return (
      <div className="grid">
        <div className="col-12">
          <div className="p-card p-4">
            <div className="flex flex-column justify-content-center align-items-center p-5">
              <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" />
              <p className="text-600 mt-3 m-0">Cargando contenido</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="grid">
        <div className="col-12">
          <div className="p-card p-4 text-center">
            <h2>Venue no encontrado</h2>
            <Button label="Volver" icon="pi pi-arrow-left" onClick={() => navigate("/venues")} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid">
      <Breadcrumb items={[{ label: "Venues", to: "/venues" }, { label: venue?.name ?? "Detalle" }]} />
      <div className="col-12">
        <div className="p-card p-4">
          <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
            <div className="flex align-items-center gap-3 flex-wrap">
              <Button
                icon="pi pi-arrow-left"
                rounded
                text
                onClick={() => navigate("/venues")}
              />
              <h2 className="m-0">{venue.name}</h2>
              <Tag
                value={getVenueStatusLabel(venue.status)}
                severity={getVenueStatusSeverity(venue.status)}
              />
            </div>
            <Button
              label="Editar"
              icon="pi pi-pencil"
              onClick={() => navigate(`/venues/${venue.id}/edit`)}
            />
          </div>
          <BenefitsMetricsTenantScopeHeader tenantId={tenantScope.tenantId} />

          <Panel header="Información Básica" className="mb-4" toggleable>
            <div className="grid">
              <div className={tenantScope.role === "SUPER_ADMIN" ? "col-12 md:col-6" : "col-12"}>
                <div className="text-color-secondary text-sm">Dirección</div>
                <div className="font-medium text-lg">{venue.address}</div>
              </div>
              {tenantScope.role === "SUPER_ADMIN" && (
                <div className="col-12 md:col-6">
                  <div className="text-color-secondary text-sm">Tenants</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {venue.tenantIds?.map((t) => (
                      <Tag key={t} value={tenantMap.get(String(t)) || t} severity="info" />
                    ))}
                  </div>
                </div>
              )}
              <div className="col-12 md:col-6">
                <div className="text-color-secondary text-sm">Latitud</div>
                <div className="font-medium">{venue.latitude}</div>
              </div>
              <div className="col-12 md:col-6">
                <div className="text-color-secondary text-sm">Longitud</div>
                <div className="font-medium">{venue.longitude}</div>
              </div>
            </div>
          </Panel>

          <Panel header="Información Operativa" className="mb-4" toggleable>
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="text-color-secondary text-sm">Categorías de evento</div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {venue.eventCategories?.length ? (
                    venue.eventCategories.map((c) => (
                      <Tag key={c} value={getEventCategoryLabel(c)} />
                    ))
                  ) : (
                    <span className="text-color-secondary">Sin categorías</span>
                  )}
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="text-color-secondary text-sm">Capacidad máxima</div>
                <div className="font-medium text-lg">
                  {venue.maxCapacity !== null ? venue.maxCapacity : "—"}
                </div>
              </div>
              <div className="col-12">
                <div className="text-color-secondary text-sm mb-2">Amenities</div>
                <div className="flex flex-wrap gap-2">
                  {venue.amenities?.length ? (
                    venue.amenities.map((a) => (
                      <Tag key={a} value={getAmenityLabel(a)} severity="success" />
                    ))
                  ) : (
                    <span className="text-color-secondary">Sin amenities</span>
                  )}
                </div>
              </div>
            </div>
          </Panel>

          <Panel header="Contactos" className="mb-4" toggleable>
        {venue.contacts && venue.contacts.length > 0 ? (
          venue.contacts.map((contact, index) => (
            <div key={index}>
              {index > 0 && <Divider />}
              <div className="grid">
                <div className="col-12">
                  <span className="text-sm font-semibold text-primary">
                    {index === 0 ? "Contacto Principal" : `Contacto #${index + 1}`}
                  </span>
                </div>
                <div className="col-12 sm:col-6 lg:col-3">
                  <div className="text-color-secondary text-sm">Nombre</div>
                  <div className="font-medium">{contact.name}</div>
                </div>
                <div className="col-12 sm:col-6 lg:col-3">
                  <div className="text-color-secondary text-sm">Rol</div>
                  <div className="font-medium">{contact.role}</div>
                </div>
                <div className="col-12 sm:col-6 lg:col-3">
                  <div className="text-color-secondary text-sm">Email</div>
                  <div className="font-medium">{contact.email}</div>
                </div>
                <div className="col-12 sm:col-6 lg:col-3">
                  <div className="text-color-secondary text-sm">Teléfono</div>
                  <div className="font-medium">{contact.phone}</div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-color-secondary">Sin contactos registrados.</p>
        )}
      </Panel>

          <Panel header="Métricas de Calidad" className="mb-4" toggleable>
        <div className="grid">
          <div className="col-12 sm:col-6 lg:col-3">
            <div className="text-color-secondary text-sm mb-1">Calificación promedio</div>
            <div className="flex align-items-center gap-2">
              <i className="pi pi-star-fill text-yellow-500 text-xl" />
              <span className="text-2xl font-bold">
                {venue.rating?.average?.toFixed(1) || "—"}
              </span>
              <span className="text-color-secondary">
                ({venue.rating?.count || 0} valoraciones)
              </span>
            </div>
          </div>
          <div className="col-12">
            <div className="text-color-secondary text-sm mb-2">Últimos comentarios</div>
            {venue.comments && venue.comments.length > 0 ? (
              <div className="flex flex-column gap-2">
                {venue.comments.slice(0, 10).map((comment) => (
                  <div key={comment.id} className="surface-100 border-round p-3">
                    <div className="flex justify-content-between align-items-center mb-1">
                      <span className="font-semibold">{comment.userName}</span>
                      <div className="flex align-items-center gap-1">
                        <i className="pi pi-star-fill text-yellow-500 text-sm" />
                        <span>{comment.rating}</span>
                      </div>
                    </div>
                    <p className="m-0 text-color-secondary">{comment.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-color-secondary">
                No hay comentarios aún. Las valoraciones serán visibles cuando los usuarios califiquen este venue desde la App.
              </p>
            )}
          </div>
        </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};
